/**
 * Brand portal requests and results of AI 開店助手 (onboarding): a draft of the brand's profile, shops, colours
 * and levels from its website or pasted text. Nothing is saved by the AI call; 套用 saves the ticked parts
 * with the usual endpoints (settings sections, /tiers), using onboardingApply() below.
 */
import { z } from 'zod';
import type { BrandSettings, StoreInfo } from './contract.ts';
import type { LocalizedText } from './i18n.ts';
import type { TierInput } from './schemas.ts';

/** POST /api/portal/ai/setup: the brand's website, or text pasted from it (About page, menu, shop list). */
export const aiSetupSchema = z
  .object({
    url: z.string().trim().max(2000).optional(),
    text: z.string().trim().max(100_000).optional(),
  })
  .refine((v) => !v.url !== !v.text, { message: 'give a website or pasted text, not both', path: ['url'] });
export type AiSetupInput = z.infer<typeof aiSetupSchema>;

/** A suggested level; the list goes from the entry level (no upgrade spend) up. */
export interface OnboardingLevel {
  name: LocalizedText;
  benefits: LocalizedText;
  /** HK$ spent to reach the level; null for the entry level (or not suggested). */
  upgradeSpend: number | null;
  /** 儲分比例: every `amount` dollars earn `points`; null = not suggested. */
  earn: { amount: number; points: number } | null;
}

/** The model's draft. Empty text or colour = the page did not say; levels are its suggestion for the business. */
export interface OnboardingSuggestion {
  businessType: string;
  /** `intro` is the short one, saved as the 「SEO／分享」 description. */
  profile: { name: LocalizedText; intro: LocalizedText; about: LocalizedText };
  /** Only shops the page states. */
  stores: Array<Omit<StoreInfo, 'id' | 'mapUrl'>>;
  /** #RRGGBB, or '' for none. */
  theme: { primary: string; accent: string };
  levels: OnboardingLevel[];
  /** Why the levels and earn ratios suit the business (Traditional Chinese). */
  levelsNote: string;
}

/** What code (not the model) read from the page. */
export interface OnboardingSource {
  /** The page read, after redirects; null for pasted text. */
  url: string | null;
  title: string;
  description: string;
  themeColor: string | null;
  ogImage: string | null;
  /** Colours used most in the page's own styles, most used first. */
  colors: string[];
  /** Characters of page text given to the model. */
  chars: number;
}

export interface AiSetupResult {
  source: OnboardingSource;
  suggestion: OnboardingSuggestion;
}

/** The parts ticked on the page. */
export interface OnboardingPicks {
  name: boolean;
  intro: boolean;
  about: boolean;
  colors: boolean;
  /** Indexes into suggestion.stores. */
  stores: number[];
  /** Indexes into suggestion.levels; 0 is the entry level. */
  levels: number[];
}

/**
 * The saves 套用 makes. Settings sections are whole (PUT /settings/:section) with only the ticked parts changed:
 * texts keep the languages the suggestion left empty, shops are added after the current ones. New levels are
 * POST /tiers highest first, each upgrading to the one made before it. `entry` is the current entry level with
 * the ticked changes, saved last (PUT /tiers/:id); when it upgrades to nothing it then upgrades to the lowest new level.
 */
export interface OnboardingApply {
  profile: BrandSettings['profile'] | null;
  seo: BrandSettings['seo'] | null;
  theme: BrandSettings['theme'] | null;
  newTiers: TierInput[];
  entry: TierInput | null;
}

const filled = (t: LocalizedText) => Object.values(t).some((v) => v?.trim());

/** `current` with the languages `next` has text for replaced. */
const merge = (current: LocalizedText, next: LocalizedText): LocalizedText => ({ ...current, ...Object.fromEntries(Object.entries(next).filter(([, v]) => v?.trim())) });

const key = (v: string | undefined) => (v ?? '').replace(/\s+/g, '').toLowerCase();

/** A shop with the same name (in any language) is already listed. */
export const knownStore = (stores: StoreInfo[], s: { name: LocalizedText }) =>
  stores.some((x) => Object.entries(s.name).some(([l, v]) => !!key(v) && key(x.name[l as keyof LocalizedText]) === key(v)));

/** What starts ticked: whatever only fills in or adds (new shops, new levels for a brand with just its entry level). */
export function defaultPicks(current: BrandSettings, s: OnboardingSuggestion, tierCount: number): OnboardingPicks {
  const fills = (now: LocalizedText, next: LocalizedText) => filled(next) && !filled(now);
  return {
    name: fills(current.profile.name, s.profile.name),
    intro: fills(current.seo.description, s.profile.intro),
    about: fills(current.profile.about, s.profile.about),
    colors: false,
    stores: s.stores.flatMap((x, i) => (knownStore(current.profile.stores, x) ? [] : [i])),
    levels: tierCount <= 1 ? s.levels.flatMap((_, i) => (i > 0 ? [i] : [])) : [],
  };
}

/**
 * The saves for the ticked parts. `tiers` (native brands): the current entry level as a request body, the sort
 * number for the first new level and the colours new levels take in turn.
 */
export function onboardingApply(
  current: BrandSettings,
  s: OnboardingSuggestion,
  picks: OnboardingPicks,
  tiers: { entry: TierInput | null; nextSort: number; palette: readonly string[] },
): OnboardingApply {
  const stores = picks.stores.flatMap((i) => (s.stores[i] ? [{ ...s.stores[i] }] : []));
  const profile =
    picks.name || picks.about || stores.length
      ? {
          ...current.profile,
          name: picks.name ? merge(current.profile.name, s.profile.name) : current.profile.name,
          about: picks.about ? merge(current.profile.about, s.profile.about) : current.profile.about,
          stores: [...current.profile.stores, ...stores],
        }
      : null;
  const seo = picks.intro ? { ...current.seo, description: merge(current.seo.description, s.profile.intro) } : null;
  const c = current.theme.colors;
  const theme = picks.colors && (s.theme.primary || s.theme.accent) ? { ...current.theme, colors: { ...c, primary: s.theme.primary || c.primary, accent: s.theme.accent || c.accent } } : null;

  const entry = tiers.entry;
  const fallbackEarn = entry?.earn ?? { amount: 1, points: 1 };
  const upper = [...new Set(picks.levels)].filter((i) => i > 0 && s.levels[i]).sort((a, b) => a - b);
  const newTiers: TierInput[] = entry
    ? upper
        .map((i, k) => {
          const l = s.levels[i]!;
          return {
            name: l.name,
            description: l.benefits,
            color: tiers.palette[i % tiers.palette.length] ?? '#5E8C6A',
            cardImageKey: null,
            iconKey: null,
            sort: Math.min(1000, tiers.nextSort + k),
            isDefault: false,
            upgradeSpend: l.upgradeSpend,
            retainSpend: null,
            validityMonths: null,
            upgradeToId: null,
            downgradeToId: null,
            earn: l.earn ?? fallbackEarn,
          };
        })
        .reverse()
    : [];
  const first = picks.levels.includes(0) ? s.levels[0] : undefined;
  const entryInput =
    entry && (first || (newTiers.length && entry.upgradeToId === null))
      ? { ...entry, ...(first && { name: merge(entry.name, first.name), description: merge(entry.description, first.benefits), earn: first.earn ?? entry.earn }) }
      : null;
  return { profile, seo, theme, newTiers, entry: entryInput };
}
